<template>
    <section class="core-features-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
            </div>

            <div class="features-showcase-container">
                <div
                    v-for="(feature, index) in features"
                    :key="index"
                    class="feature-showcase"
                    :class="{ reverse: index % 2 !== 0 }"
                >
                    <div class="feature-content">
                        <template v-if="index % 2 === 0">
                            <div class="feature-text">
                                <h3 class="feature-title">{{ feature.title }}</h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="feature.videoUrl"
                                    :poster="getResourceThumbnail(feature.videoUrl)"
                                    :title="feature.title"
                                    :description="feature.description"
                                />
                            </div>
                        </template>
                        <template v-else>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="feature.videoUrl"
                                    :poster="getResourceThumbnail(feature.videoUrl)"
                                    :title="feature.title"
                                    :description="feature.description"
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">{{ feature.title }}</h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { getResourceThumbnail } from '~/utils/common'

interface Feature {
    title: string
    description: string
    videoUrl: string
}

interface Props {
    title: string
    features: Feature[]
}

defineProps<Props>()
</script>

<style lang="less" scoped>
.core-features-section {
    padding: 11.25rem 0;
    background-color: #120e11;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

.features-showcase-container {
    max-width: 1200px;
    margin: 0 auto;
    border-radius: 20px;
    overflow: hidden;

    .feature-showcase:first-of-type {
        background: linear-gradient(180deg, #341012, #220f11, #190f11, #100e11);
    }

    .feature-showcase:not(:first-of-type):not(:last-of-type) {
        background-color: #120e11;
    }

    .feature-showcase:last-of-type {
        background: linear-gradient(0deg, #341012, #220f11, #190f11, #100e11);
        border-radius: 20px;
    }
}

.feature-showcase {
    padding: 5rem;

    &:last-of-type {
        margin-bottom: 4rem;
    }

    .image-container {
        border-radius: 20px;
        overflow: hidden;
    }
}

.feature-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
}

.feature-text {
    padding: 2rem;
}

.feature-title {
    font-size: 2.25rem;
    font-weight: 500;
    margin-bottom: 1.5rem;
    color: white;
    line-height: 1.3;
}

.feature-description {
    color: #adadad;
    line-height: 1.6;
    font-size: 1.125rem;
    margin: 0;
}

.feature-video {
    position: relative;
}

@media (max-width: 768px) {
    .core-features-section {
        padding: 5rem 0;
    }

    .section-title {
        font-size: 2rem;
        padding: 0 1rem;
    }

    .section-header {
        margin-bottom: 2rem;
        padding: 0 1rem;
    }

    .features-showcase-container {
        padding: 0 1rem;
    }

    .feature-content {
        grid-template-columns: 1fr;
        gap: 2rem;
    }

    .feature-showcase {
        padding: 3rem 1.5rem;
    }

    .feature-text {
        padding: 1rem;
    }

    .feature-title {
        font-size: 1.5rem;
        margin-bottom: 1rem;
    }

    .feature-description {
        font-size: 1rem;
    }
}

@media (max-width: 480px) {
    .core-features-section {
        padding: 3rem 0;
    }

    .section-title {
        font-size: 1.5rem;
    }

    .section-header {
        margin-bottom: 1.5rem;
    }

    .features-showcase-container {
        padding: 0 0.75rem;
    }

    .feature-showcase {
        padding: 2rem 1rem;
    }

    .feature-title {
        font-size: 1.25rem;
    }

    .feature-description {
        font-size: 0.875rem;
    }
}
</style>

